import { FC, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Divider, List, ListItem, ListItemSecondaryAction, ListItemText, Stack, Switch, TextField, Typography } from '@mui/material';
import { DomainChip } from '../domain/domain-chip';
import { Activity, ActivityType } from '../../graphql/generated/graphql.ts';
import { TimeSelection } from './time-selection.tsx';
import { useCreateActivityMutation } from '../../graphql/generated/operations/activity.ts';

const getType = (type: string) => {
  switch (type) {
    case ActivityType.Pleasant:
      return 'Pleasant';
    case ActivityType.Meaningful:
      return 'Challenging / Meaningful';
    case ActivityType.Social:
      return 'Social Connection';
  }
};

export interface ReplaceActivityProps {
  activity: Activity;
  alreadyRepeated?: boolean;
}

export const ReplaceActivity: FC<ReplaceActivityProps> = ({ activity, alreadyRepeated }) => {
  const [checked, setChecked] = useState(false);
  const [subgoal, setSubgoal] = useState(activity.subgoal);
  const [schedule, setSchedule] = useState(activity.schedule);
  const [createActivity, { data, loading, error }] = useCreateActivityMutation();

  if (!activity) {
    return null;
  }

  const handleReplan = () => {
    createActivity({
      variables: {
        input: {
          description: activity.description,
          domainID: activity.domainID,
          schedule: schedule.map(({ day, period }) => ({ day, period })),
          subgoal: subgoal,
          type: activity.type,
          offset: Math.floor(new Date().getTimezoneOffset() / 60) // Timezone offset in hours
        }
      }
    }).catch((e) => console.error('Replan error:', e));
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">{activity.description}</Typography>
          <Box>
            <DomainChip domainId={activity.domainID} />
          </Box>
          {activity.subgoal && <Typography>Subgoal: {activity.subgoal}</Typography>}
          <Typography>Priority: {getType(activity.type)}</Typography>
          <Divider />
          <List>
            {error && (
              <ListItem>
                <Alert sx={{ width: '100%' }} severity="error">
                  {error.message}
                </Alert>
              </ListItem>
            )}
            {(data || alreadyRepeated) && (
              <ListItem>
                <Alert sx={{ width: '100%' }} severity="success">
                  Activity scheduled for next week!
                </Alert>
              </ListItem>
            )}
            {!data && !alreadyRepeated && (
              <ListItem>
                <ListItemText primary="Repeat This Activity Next Week" />
                <ListItemSecondaryAction>
                  <Switch disabled={loading} checked={checked} onChange={() => setChecked(!checked)} />
                </ListItemSecondaryAction>
              </ListItem>
            )}
            {checked && !data && !alreadyRepeated && (
              <>
                <ListItem>
                  <ListItemText primary="Update Subgoal" />
                </ListItem>
                <ListItem>
                  <TextField disabled={loading} fullWidth label="New Subgoal" value={subgoal} onChange={(e) => setSubgoal(e.target.value)} />
                </ListItem>
                <ListItem>
                  <TimeSelection disabled={loading} defaultSchedule={activity.schedule} onChange={(s) => setSchedule(s)} />
                </ListItem>
                <Button disabled={loading} variant="contained" fullWidth onClick={handleReplan}>
                  Confirm for Next Week
                </Button>
              </>
            )}
          </List>
        </Stack>
      </CardContent>
    </Card>
  );
};
